import React, {useCallback} from 'react';
import AccordionDetails from '@mui/material/AccordionDetails';
import {SessionInfoTable, SessionInfoTableRow} from 'module/SessionInfo/component/modal/SessionInfoTable';
import {getSisDateAsISO} from 'util/SisDateTime';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import {Modal as Modal} from 'module/Modal/component/Modal';
import Button from '@mui/material/Button';
import {SwitchUserComponent} from 'module/SessionInfo/component/userInfo/SwitchUserComponent';
import {SessionInfoKostComponent} from 'module/SessionInfo/component/kost/SessionInfoKostComponent';
import {VorgesetzterUserSelect} from 'module/SessionInfo/component/userInfo/VorgesetzterUserSelect';
import {MandantSelect} from 'module/SessionInfo/component/userInfo/MandantSelect';
import {EwsRepSelectList} from 'module/SessionInfo/component/userInfo/EwsRepSelectList';
import {PersonInfoList} from 'module/PersonInfo/component/PersonInfoList';
import withStyles from '@mui/styles/withStyles';
import {logout, openHelp} from 'module/SessionInfo/api/SessionInfoAPI';
import DialogActions from '@mui/material/DialogActions';
import {useDispatch, useSelector} from 'react-redux';
import {DeleteLocalCacheButton} from 'component/DeleteLocalCacheButton';
import Paper from '@mui/material/Paper';
import {ModalKey} from 'module/Modal/type/ModalKey';
import {selSessionInfoData, selSessionInfoExpanded} from 'module/SessionInfo/selector/SessionInfoSelector';
import {sessionInfoExpandAction} from 'module/SessionInfo/action/SessionInfoAction';
import {Accordion, AccordionProps, AccordionSummary, useTheme} from '@mui/material'
import {css} from '@emotion/react'
import {VlErrorList} from 'module/PersonInfo/component/VlErrorList'
import {ChangePasswordComponent} from 'module/SessionInfo/component/password/ChangePasswordComponent'

const cssMediumFont = css`
	font-size: medium;
`
const cssVlErrorList = css`
	max-height: 500px;
	overflow: auto;
`
const cssAccordion = css`
	&.Mui-expanded {
		margin: 0;
	}
`

const DialogActionsPaper = withStyles({root: {position: 'sticky', bottom: 0}})(Paper);

const MODAL_KEY = ModalKey.SessionInfo;
const panelProps = {TransitionProps: {unmountOnExit: true}};

export const SessionInfoModal = React.memo(() => {
	const theme = useTheme()
	const cssColor = css`
		background: ${theme.palette.primary.main};
		color: ${theme.palette.getContrastText(theme.palette.primary.main)};
	`
	const data = useSelector(selSessionInfoData);
	const expanded = useSelector(selSessionInfoExpanded)
	const expandedUser = expanded.User
	const expandedCostCenter = expanded.CostCenter
	const expandedCalculation = expanded.Calculation
	const expandedPersonalInfo = expanded.PersonalInfo
	const expandedPassword = expanded.Password

	const dispatch = useDispatch();
	const expandedUserCb = useCallback<Required<AccordionProps>['onChange']>((evt, value) => dispatch(sessionInfoExpandAction({name: 'User', expanded: Boolean(value)})), [])
	const expandedCostCenterCb = useCallback<Required<AccordionProps>['onChange']>((evt, value) => dispatch(sessionInfoExpandAction({name: 'CostCenter', expanded: Boolean(value)})), [])
	const expandedCalculationCb = useCallback<Required<AccordionProps>['onChange']>((evt, value) => dispatch(sessionInfoExpandAction({name: 'Calculation', expanded: Boolean(value)})), [])
	const expandedPersonalInfoCb = useCallback<Required<AccordionProps>['onChange']>((evt, value) => dispatch(sessionInfoExpandAction({name: 'PersonalInfo', expanded: Boolean(value)})), [])
	const expandedPasswordCb = useCallback<Required<AccordionProps>['onChange']>((evt, value) => dispatch(sessionInfoExpandAction({name: 'Password', expanded: Boolean(value)})), [])

	if (!data || !data.evoAppsUser) return null;
	const {
		evoAppsUser, costCenter, calculation, personalInfo, ewsRepList,
		vorgztUserRendered, mailVertretungRendered, passwordPanelRendered,
		visiblePersNrInfo,
	} = data;
	const showVorgesetzterSelect = vorgztUserRendered === true;
	const showAltMandantSelect = evoAppsUser.defaultMandant != null;
	const {mandant, alternativeMandants, defaultMandant, vorgesetzterUser} = evoAppsUser;

	return (
		<Modal
			modalKey={MODAL_KEY}
			disableAutoFocus={true}
			title={'Benutzerinformationen'}
		>
			<Accordion
				css={cssAccordion}
				{...panelProps}
				expanded={expandedUser}
				onChange={expandedUserCb}
			>
				<AccordionSummary css={cssColor} expandIcon={<ExpandMoreIcon css={cssColor}/>}>
					Benutzerdaten
				</AccordionSummary>
				<AccordionDetails>
					<form noValidate autoComplete="off" style={{width: '100%'}}>
						<SessionInfoTable>
							<SessionInfoTableRow label={'Benutzer'}>
								<SwitchUserComponent evoAppsUser={evoAppsUser}/>
							</SessionInfoTableRow>
							{showVorgesetzterSelect &&
								<SessionInfoTableRow label={'Vorgesetzter'}>
									<VorgesetzterUserSelect vorgesetzterUser={vorgesetzterUser}/>
								</SessionInfoTableRow>
							}
							{showAltMandantSelect &&
								<SessionInfoTableRow label={'Mandant'}>
									<MandantSelect
										mandant={mandant}
										alternativeMandants={alternativeMandants}
										defaultMandant={defaultMandant}/>
								</SessionInfoTableRow>
							}
							{mailVertretungRendered &&
								<SessionInfoTableRow label={'E-Mail Vertretung'}>
									<EwsRepSelectList ewsRepList={ewsRepList}/>
								</SessionInfoTableRow>
							}
							<SessionInfoTableRow label={'Benutzergruppe'}>
								{evoAppsUser.userGroup.id}
							</SessionInfoTableRow>
							<SessionInfoTableRow label={'Person'}>
								<span dangerouslySetInnerHTML={{__html: visiblePersNrInfo}}/>
							</SessionInfoTableRow>
							<SessionInfoTableRow label={'E-Mail'}>
								{evoAppsUser.email}
							</SessionInfoTableRow>
							<SessionInfoTableRow label={'App-Cache'}>
								<DeleteLocalCacheButton/>
							</SessionInfoTableRow>
						</SessionInfoTable>
					</form>
				</AccordionDetails>
			</Accordion>
			{costCenter.panelRendered &&
				<Accordion
					css={cssAccordion}
					{...panelProps}
					expanded={expandedCostCenter}
					onChange={expandedCostCenterCb}
				>
					<AccordionSummary css={cssColor} expandIcon={<ExpandMoreIcon css={cssColor}/>}>
						Aktuelle Kontierung
					</AccordionSummary>
					<AccordionDetails>
						<SessionInfoKostComponent kostInfo={costCenter}/>
					</AccordionDetails>
				</Accordion>
			}
			{calculation.panelRendered &&
				<Accordion
					css={cssAccordion}
					{...panelProps}
					expanded={expandedCalculation}
					onChange={expandedCalculationCb}
				>
					<AccordionSummary css={cssColor} expandIcon={<ExpandMoreIcon css={cssColor}/>}>
						Gültig erfasst bis {getSisDateAsISO(calculation && calculation.clearedToDate)}
					</AccordionSummary>
					<AccordionDetails>
						<div css={cssVlErrorList}>
							<VlErrorList models={calculation.errorMessages}/>
						</div>
					</AccordionDetails>
				</Accordion>
			}
			{personalInfo.panelRendered &&
				<Accordion
					css={cssAccordion}
					{...panelProps}
					expanded={expandedPersonalInfo}
					onChange={expandedPersonalInfoCb}
				>
					<AccordionSummary css={cssColor} expandIcon={<ExpandMoreIcon css={cssColor}/>}>
						Information
					</AccordionSummary>
					<AccordionDetails>
						<div css={cssMediumFont}>
							<PersonInfoList models={personalInfo.infos}/>
						</div>
					</AccordionDetails>
				</Accordion>
			}
			{passwordPanelRendered &&
				<Accordion
					css={cssAccordion}
					{...panelProps}
					expanded={expandedPassword}
					onChange={expandedPasswordCb}
				>
					<AccordionSummary css={cssColor} expandIcon={<ExpandMoreIcon css={cssColor}/>}>
						Passwort
					</AccordionSummary>
					<AccordionDetails>
						<ChangePasswordComponent/>
					</AccordionDetails>
				</Accordion>
			}
			<DialogActionsPaper>
				<DialogActions>
					<Button size={'large'} onClick={logout}><PowerSettingsNewIcon/>&nbsp;Abmelden</Button>
					<Button size={'large'} onClick={openHelp}><HelpOutlinedIcon/>&nbsp;Hilfe</Button>
				</DialogActions>
			</DialogActionsPaper>
		</Modal>
	)
});